// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Link} from "react-router-dom";
import {Button, Col, List, Modal, Popconfirm, Popover, Row, Select, Table, Tooltip} from "antd";
import {EditOutlined, ExclamationCircleFilled} from "@ant-design/icons";
import moment from "moment";
import * as Setting from "../../conf/Setting";
import * as ApplicationBackend from "../../services/ApplicationBackend";
import i18next from "i18next";
import BaseListPage from "../../components/basic/BaseListPage";
import {v4 as uuidv4} from "uuid";
import * as OrganizationBackend from "../../services/OrganizationBackend";

class ApplicationListPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      modalVisible: false,
      selectedOrganization: "",
      selectedOrganName: "",
      organizations: [],
      confirmLoading: false,
    };
  }

  newApplication(organUid, organName) {
    const randomName = Setting.getRandomName();
    return {
      owner: Setting.Admin, // this.props.account.applicationname,
      uid: uuidv4(),
      isManagement: false,
      createdTime: moment().format("yyyy-MM-DD HH:mm:ss"),
      updatedTime: moment().format("yyyy-MM-DD HH:mm:ss"),
      displayName: `新应用 - ${randomName}`,
      logo: "",
      enablePassword: true,
      enableSignUp: true,
      enableSigninSession: true,
      enableCodeSignin: true,
      enablePhoneCodeSignin: true,
      enableEmailCodeSignin: false,
      enableSamlCompress: false,
      providers: [
        {uid: "provider_localhost_save_default", displayName: "本地文件存储", canSignUp: false, canSignIn: false, canUnlink: false, prompted: false, alertType: "None", visible: false},
        {uid: "provider_captcha_default", displayName: "人机验证", canSignUp: false, canSignIn: false, canUnlink: false, prompted: false, alertType: "None", visible: true},
      ],
      signupItems: [
        {name: "ID", visible: false, required: true, rule: "Random"},
        {name: "Username", visible: true, required: true, rule: "None"},
        {name: "Display name", visible: true, required: true, rule: "None"},
        {name: "Password", visible: true, required: true, rule: "None"},
        {name: "Confirm password", visible: true, required: true, rule: "None"},
        {name: "Email", visible: true, required: true, rule: "Normal"},
        {name: "Phone", visible: true, required: true, rule: "None"},
        {name: "Agreement", visible: true, required: true, rule: "None"},
      ],
      cert: "cert-built-in",
      redirectUris: ["http://localhost:9000/callback"],
      tokenFormat: "JWT",
      expireInHours: 24 * 7,
      refreshExpireInHours: 24 * 30,
      formOffset: 8,
      organName: organName,
      organization: organUid,
    };
  }

  UNSAFE_componentWillMount() {
    super.UNSAFE_componentWillMount();
    this.getOrganizations();
  }

  getOrganizations() {
    OrganizationBackend.getOrganizations(Setting.Admin)
      .then((res) => {
        if (res.msg === undefined) {
          const org = res.filter(organization => !Setting.isAdminOrganization(organization.uid)).reverse();
          this.setState({
            organizations: org,
          });
          if (this.state.mode === "add") {
            this.updateRoleField("owner", org.length > 0 ? org[0].uid : "");
          }
        } else {
          this.setState({
            organizations: [],
          });
        }
      });
  }

  addApplication() {
    if (!this.state.selectedOrganization) {
      Setting.showMessage("error", i18next.t("application:Please select an organizaion for application"));
      return;
    }
    const newApplication = this.newApplication(this.state.selectedOrganization, this.state.selectedOrganName);
    this.setState({confirmLoading: true});
    ApplicationBackend.addApplication(newApplication)
      .then((res) => {
        this.setState({confirmLoading: false});
        if (res.status === "ok") {
          this.props.history.push({pathname: `/applications/${newApplication.uid}`, mode: "add"});
        } else {
          Setting.showResponseMessage(res.msg);
        }
      }
      )
      .catch(error => {
        Setting.showResponseMessage(error);
        this.setState({confirmLoading: false});
      });
  }

  deleteApplication(i) {
    ApplicationBackend.deleteApplication(this.state.data[i])
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("application:Application deleted successfully"));
          this.handleDelete();
        } else {
          Setting.showResponseMessage(res.msg);
        }
      }
      )
      .catch(error => {
        Setting.showResponseMessage(error);
      });
  }

  renderTable(applications) {
    const columns = [
      // {
      //   title: i18next.t("general:Name"),
      //   dataIndex: "name",
      //   key: "name",
      //   width: "150px",
      //   fixed: "left",
      //   sorter: true,
      //   ...this.getColumnSearchProps("name"),
      //   render: (text, record, index) => {
      //     return (
      //       <Link to={`/applications/${text}`}>
      //         {text}
      //       </Link>
      //     );
      //   },
      // },
      {
        title: i18next.t("general:Organization"),
        dataIndex: "organName",
        key: "organName",
        width: "150px",
        sorter: true,
        ...this.getColumnSearchProps("organName"),
        render: (text, record, index) => {
          return (
            record.organization !== "-1" ? text : i18next.t("application:Organization empty")
          );
        },
      },
      {
        title: i18next.t("application:Display name"),
        dataIndex: "displayName",
        key: "displayName",
        // width: '100px',
        sorter: true,
        ...this.getColumnSearchProps("displayName"),
        render: (text, record, index) => {
          return (
            record.organization !== "-1" ?
              <Link to={`/applications/${record.uid}`}>
                {text}
              </Link>
              :
              <Link to={`/applications/${record.uid}`} style={{color: "#faad14"}}>
                {text}
                {
                  record.organization !== "-1" ? null :
                    <Popover content={i18next.t("application:Associated organization is empty")}>
                      <ExclamationCircleFilled style={{color: "#faad14", marginLeft: 5}} />
                    </Popover>
                }
              </Link>
          );
        },
      },
      {
        title: i18next.t("general:Created time"),
        dataIndex: "createdTime",
        key: "createdTime",
        width: "160px",
        sorter: true,
        render: (text, record, index) => {
          return Setting.getFormattedDate(text);
        },
      },
      {
        title: "Logo",
        dataIndex: "logo",
        key: "logo",
        width: "100px",
        render: (text, record, index) => {
          return (
            record?.systemLogo !== "" ?
              // <div className={`icon-group ${record?.sysLogoBg}`} style={{width: 44, height: 44}}>
              //   <i className={`iconfont ${record?.systemLogo}`} style={{fontSize: 20}} />
              // </div>
              <div style={{display: "flex", justifyContent: "center"}}>
                <li className="icon-container"
                >
                  <span className={["app-icon", record?.sysLogoBg].join(" ")}></span>
                </li>
              </div>
              :
              <div style={{textAlign: "center"}}><img src={text === "" ? require("../../assets/images/default_icon.png") : text} alt={""} width={33} height={33} /></div>
          );
        },
      },
      {
        title: i18next.t("general:Providers"),
        dataIndex: "providers",
        key: "providers",
        // ...this.getColumnSearchProps("providers"),
        // width: '600px',
        render: (text, record, index) => {
          const providers = text.filter(item => item.visible === true);
          if (providers.length === 0) {
            return "(empty)";
          }

          const half = Math.floor((providers.length + 1) / 2);

          const getList = (providers) => {
            return (
              <List
                size="small"
                locale={{emptyText: " "}}
                dataSource={providers}
                renderItem={(providerItem, i) => {
                  return (
                    <List.Item>
                      <div style={{display: "inline"}}>
                        <Tooltip placement="topLeft" title={i18next.t("general:Edit")}>
                          <Button disabled={providerItem.uid === Setting.DefaultProvider} style={{marginRight: "5px"}} icon={<EditOutlined />} size="small" onClick={() => Setting.goToLinkSoft(this, `/providers/${providerItem.uid}`)} />
                        </Tooltip>
                        {
                          providerItem.uid === Setting.DefaultProvider ? `${providerItem.displayName}` :
                            <Link to={`/providers/${providerItem.uid}`}>
                              {providerItem.displayName}
                            </Link>
                        }
                      </div>
                    </List.Item>
                  );
                }}
              />
            );
          };

          return (
            <div>
              <Row>
                <Col span={12}>
                  {
                    getList(providers.slice(0, half))
                  }
                </Col>
                <Col span={12}>
                  {
                    getList(providers.slice(half))
                  }
                </Col>
              </Row>
            </div>
          );
        },
      },
      {
        title: i18next.t("general:Action"),
        dataIndex: "",
        key: "op",
        width: "170px",
        fixed: (Setting.isMobile()) ? "false" : "right",
        render: (text, record, index) => {
          return (
            <div>
              <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}} type="primary" onClick={() => this.props.history.push(`/applications/${record.uid}`)}>{i18next.t("general:Edit")}</Button>
              <Popconfirm
                title={`${i18next.t("application:Sure to delete application {name}").replace("{name}", record.displayName)}`}
                onConfirm={() => this.deleteApplication(index)}
                disabled={record.isManagement}
              >
                <Button style={{marginBottom: "10px"}} disabled={record.isManagement} type="danger">{i18next.t("general:Delete")}</Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ];

    const paginationProps = {
      total: this.state.pagination.total,
      showQuickJumper: true,
      showSizeChanger: true,
      showTotal: () => i18next.t("general:{total} in total").replace("{total}", this.state.pagination.total),
    };

    return (
      <div>
        <Table scroll={{x: "max-content"}} columns={columns} dataSource={applications} rowKey="uid" size="middle" bordered pagination={paginationProps}
          title={() => (
            <div>
              {i18next.t("general:Applications")}&nbsp;&nbsp;&nbsp;&nbsp;
              <Button type="primary" size="small" onClick={() => {this.setState({modalVisible: true});}}>{i18next.t("general:Add")}</Button>
            </div>
          )}
          loading={this.state.loading}
          onChange={this.handleTableChange}
        />
        {
          this.state?.modalVisible ?
            <Modal
              title={i18next.t("application:New Application")}
              visible={this.state.modalVisible}
              okText={i18next.t("general:Confirm")}
              cancelText={i18next.t("general:Cancel")}
              confirmLoading={this.state.confirmLoading}
              onOk={() => this.addApplication()}
              onCancel={() => {this.setState({modalVisible: false});}}
            >
              <p>{i18next.t("application:Please select an organizaion for application")}</p>
              <Select virtual={false} style={{width: "100%"}} value={this.state.selectedOrganization} onChange={((value, option) => {
                this.setState({selectedOrganization: value, selectedOrganName: option.children});
              })}>
                {
                  this.state.organizations.map((organization, index) => <Select.Option key={index} value={organization.uid}>{organization.displayName}</Select.Option>)
                }
              </Select>
            </Modal>
            : null
        }
      </div>
    );
  }

  fetch = (params = {}) => {
    const field = params.searchedColumn, value = params.searchText;
    const sortField = params.sortField, sortOrder = params.sortOrder;
    this.setState({loading: true});
    ApplicationBackend.getApplications(Setting.Admin, params.pagination.current, params.pagination.pageSize, field, value, sortField, sortOrder)
      .then((res) => {
        if (res.status === "ok") {
          this.setState({
            loading: false,
            data: res.data,
            pagination: {
              ...params.pagination,
              total: res.data2,
            },
            searchText: params.searchText,
            searchedColumn: params.searchedColumn,
          });
        }
      });
  };
}

export default ApplicationListPage;
